(() => {
  'use strict';

  const LEVEL_NAMES = {
    new_creator: 'New Creator',
    elsa_creator: 'Elsa Creator',
    elsa_pro: 'Elsa Pro',
    elsa_ambassador: 'Elsa Ambassador'
  };

  let client = null;
  let hub = null;
  let currentUser = null;
  let currentAccount = null;
  let currentProfile = null;
  let currentAddress = null;
  let currentIsAdmin = false;
  let portalLoaded = false;
  let portalData = { campaigns: [], applications: [], collaborations: [], submissions: [], notifications: [], deliverables: [] };
  let adminData = { accounts: [], profiles: [], addresses: [], campaigns: [], applications: [], collaborations: [], submissions: [], adminProfiles: [], finances: [], shipmentItems: [], contentMetrics: [] };


  const qs = (selector, scope = document) =>
    scope.querySelector(selector);

  const qsa = (selector, scope = document) =>
    [...scope.querySelectorAll(selector)];


  const showView = (viewName) => {
    qsa('[data-ech-view]', hub).forEach((view) => {
      const active = view.dataset.echView === viewName;

      view.hidden = !active;
    });
  };


  const setMessage = (element, message, type = 'error') => {
    if (!element) return;

    element.hidden = !message;
    element.textContent = message || '';

    element.classList.toggle(
      'is-success',
      type === 'success'
    );

    element.classList.toggle(
      'is-error',
      type === 'error'
    );
  };


  const setButtonLoading = (button, loading, text = '') => {
    if (!button) return;

    if (loading) {
      button.dataset.originalText =
        button.textContent.trim();

      button.disabled = true;
      button.textContent = text || 'Lütfen bekle...';
    } else {
      button.disabled = false;

      if (button.dataset.originalText) {
        button.textContent =
          button.dataset.originalText;
      }
    }
  };


  const formatCreatorNumber = (value) => {
    const number = Number(value || 0);

    return `ELSA-${String(number).padStart(6, '0')}`;
  };


  const formatViews = (value) => {
    const number = Number(value || 0);

    if (number >= 1000000) {
      return `${(number / 1000000)
        .toFixed(number >= 10000000 ? 0 : 1)
        .replace('.0', '')}M`;
    }

    if (number >= 1000) {
      return `${(number / 1000)
        .toFixed(number >= 100000 ? 0 : 1)
        .replace('.0', '')}K`;
    }

    return String(number);
  };


  const deliveryRate = (account) => {
    const total = Number(account?.total_deliveries || 0);
    const onTime = Number(account?.on_time_deliveries || 0);

    if (!total) return '—';

    return `${Math.round((onTime / total) * 100)}%`;
  };


  const loadCreator = async () => {
    if (!currentUser) return;

    const [accountResult, profileResult, adminResult, addressResult] = await Promise.all([
      client.from('creator_accounts').select('*').eq('user_id', currentUser.id).single(),
      client.from('creator_profiles').select('*').eq('user_id', currentUser.id).single(),
      client.from('creator_admins').select('user_id').eq('user_id', currentUser.id).maybeSingle(),
      client.from('creator_addresses').select('*').eq('user_id', currentUser.id).maybeSingle()
    ]);

    if (accountResult.error || profileResult.error) {
      console.error('Creator load error:', accountResult.error || profileResult.error);
      showView('auth');
      return;
    }

    currentAccount = accountResult.data;
    currentProfile = profileResult.data;
    currentIsAdmin = !adminResult.error && Boolean(adminResult.data);
    currentAddress = !addressResult.error ? (addressResult.data || null) : null;

    await routeCreator();
  };



  let applicationNotificationInFlight = false;

  const notifyCreatorApplication = async () => {
    if (applicationNotificationInFlight) return;

    applicationNotificationInFlight = true;

    try {
      const { error } = await client.functions.invoke(
        'notify-creator-application',
        { body: {} }
      );

      if (error) {
        console.warn(
          'Creator application notification could not be sent.',
          error
        );
      }
    } catch (error) {
      console.warn(
        'Creator application notification request failed.',
        error
      );
    } finally {
      applicationNotificationInFlight = false;
    }
  };


  const routeCreator = async () => {
    if (!currentAccount) {
      showView('auth');
      return;
    }

    if (currentIsAdmin) {
      populatePortal();
      showView('portal');
      initPortalTabs();
      await loadCreatorPortal();
      return;
    }

    switch (currentAccount.status) {
      case 'draft':
        populateApplication();
        showView('application');
        break;
      case 'pending':
        void notifyCreatorApplication();
        populatePending();
        showView('pending');
        break;
      case 'approved':
        populatePortal();
        showView('portal');
        initPortalTabs();
        await loadCreatorPortal();
        break;
      case 'rejected': {
        const reason = qs('#EchRejectedReason', hub);
        if (reason) reason.textContent = currentAccount.review_note || 'Profilini güncelleyip yeniden değerlendirmeye gönderebilirsin.';
        showView('rejected');
        break;
      }
      case 'suspended':
        showView('suspended');
        break;
      default:
        populateApplication();
        showView('application');
    }
  };


  const populateApplication = () => {
    qs('#ech-app-name', hub).value =
      currentProfile?.full_name || '';

    qs('#ech-app-email', hub).value =
      currentUser?.email || '';

    qs('#ech-app-phone', hub).value =
      currentProfile?.phone || '';

    qs('#ech-app-city', hub).value =
      currentProfile?.city || '';

    qs('#ech-app-instagram', hub).value =
      currentProfile?.instagram || '';

    qs('#ech-app-instagram-followers', hub).value =
      currentProfile?.instagram_followers ?? '';

    qs('#ech-app-tiktok', hub).value =
      currentProfile?.tiktok || '';

    qs('#ech-app-tiktok-followers', hub).value =
      currentProfile?.tiktok_followers ?? '';

    qs('#ech-app-average-views', hub).value =
      currentProfile?.average_views ?? '';

    qs('#ech-app-category', hub).value =
      currentProfile?.content_category || '';

    qs('#ech-app-hair', hub).value =
      currentProfile?.hair_type || '';

    qs('#ech-app-frequency', hub).value =
      currentProfile?.content_frequency || '';

    qs('#ech-app-sample-1', hub).value =
      currentProfile?.sample_content_1 || '';

    qs('#ech-app-sample-2', hub).value =
      currentProfile?.sample_content_2 || '';

    qs('#ech-app-portfolio', hub).value =
      currentProfile?.portfolio_url || '';

    qs('#ech-app-collabs', hub).value =
      currentProfile?.previous_collaborations || '';

    qs('#ech-app-about', hub).value =
      currentProfile?.about_creator || '';
  };


  const populatePending = () => {
    const id = qs('#EchPendingCreatorId', hub);

    if (id) {
      id.textContent =
        formatCreatorNumber(
          currentAccount?.creator_number
        );
    }
  };


  const populatePortal = () => {
    const name =
      currentProfile?.full_name ||
      currentUser?.user_metadata?.full_name ||
      'Creator';

    const firstName =
      name.trim().split(/\s+/)[0] || 'Creator';

    const initial =
      firstName.charAt(0).toUpperCase();

    const level =
      LEVEL_NAMES[currentAccount?.level] ||
      'New Creator';

    const campaigns =
      Number(currentAccount?.completed_campaigns || 0);

    const contents =
      Number(currentAccount?.approved_contents || 0);

    const views =
      Number(currentAccount?.total_views || 0);

    const delivery =
      deliveryRate(currentAccount);

    const creatorNumber =
      formatCreatorNumber(
        currentAccount?.creator_number
      );

    const values = {
      EchFirstName: firstName,
      EchSidebarName: name,
      EchSidebarLevel: level,
      EchLevelBadge: level,

      EchCompletedCampaigns: campaigns,
      EchApprovedContents: contents,
      EchTotalViews: formatViews(views),
      EchDeliveryRate: delivery,

      EchCreatorNumber: creatorNumber,

      EchPerfCampaigns: campaigns,
      EchPerfContent: contents,
      EchPerfViews: formatViews(views),
      EchPerfDelivery: delivery,

      EchProfileLevel: level,
      EchProfileName: name,
      EchProfileEmail: currentUser?.email || '',
      EchProfileInstagram:
        currentProfile?.instagram || '',
      EchProfileCity:
        currentProfile?.city || '—',
      EchProfileCategory:
        currentProfile?.content_category || '—',
      EchProfileFollowers:
        formatViews(currentProfile?.instagram_followers || 0)
    };

    Object.entries(values).forEach(
      ([id, value]) => {
        const element = qs(`#${id}`, hub);

        if (element) {
          element.textContent = value;
        }
      }
    );

    const avatar = qs('#EchAvatar', hub);
    const profileAvatar =
      qs('#EchProfileAvatar', hub);

    if (avatar) avatar.textContent = initial;

    if (profileAvatar) {
      profileAvatar.textContent = initial;
    }

    qsa('[data-admin-only]', hub).forEach((element) => {
      element.hidden = !currentIsAdmin;
    });

    populateAddress();
  };


  const initAuthTabs = () => {
    const tabs =
      qsa('[data-auth-tab]', hub);

    const panels =
      qsa('[data-auth-panel]', hub);

    const switchTab = (name) => {
      tabs.forEach((tab) => {
        tab.classList.toggle(
          'is-active',
          tab.dataset.authTab === name
        );
      });

      panels.forEach((panel) => {
        const active =
          panel.dataset.authPanel === name;

        panel.hidden = !active;
        panel.classList.toggle(
          'is-active',
          active
        );
      });
    };

    tabs.forEach((tab) => {
      tab.addEventListener('click', () => {
        switchTab(tab.dataset.authTab);
      });
    });

    qsa('[data-go-login]', hub).forEach((button) => {
      button.addEventListener('click', () => {
        showView('auth');
        switchTab('login');
      });
    });
  };


  const initLogin = () => {
    const form = qs('#EchLoginForm', hub);
    const message = qs('#EchLoginMessage', hub);

    if (!form) return;

    form.addEventListener('submit', async (event) => {
      event.preventDefault();

      const button =
        qs('button[type="submit"]', form);

      setMessage(message, '');
      setButtonLoading(
        button,
        true,
        'Giriş yapılıyor...'
      );

      const email =
        qs('#ech-login-email', hub)
          .value
          .trim()
          .toLowerCase();

      const password =
        qs('#ech-login-password', hub)
          .value;

      const { data, error } =
        await client.auth.signInWithPassword({
          email,
          password
        });

      if (error) {
        setMessage(
          message,
          'E-posta veya şifre hatalı. Hesabını henüz doğrulamadıysan e-postandaki doğrulama bağlantısını kontrol et.'
        );

        setButtonLoading(button, false);
        return;
      }

      currentUser = data.user;

      setButtonLoading(button, false);

      showView('loading');

      await loadCreator();
    });
  };


  const initRegister = () => {
    const form =
      qs('#EchRegisterForm', hub);

    const message =
      qs('#EchRegisterMessage', hub);

    if (!form) return;

    form.addEventListener('submit', async (event) => {
      event.preventDefault();

      const button =
        qs('button[type="submit"]', form);

      setMessage(message, '');

      const name =
        qs('#ech-register-name', hub)
          .value
          .trim();

      const email =
        qs('#ech-register-email', hub)
          .value
          .trim()
          .toLowerCase();

      const password =
        qs('#ech-register-password', hub)
          .value;

      const passwordConfirm =
        qs('#ech-register-password-confirm', hub)
          .value;

      if (password.length < 8) {
        setMessage(
          message,
          'Şifren en az 8 karakter olmalı.'
        );
        return;
      }

      if (password !== passwordConfirm) {
        setMessage(
          message,
          'Girdiğin şifreler eşleşmiyor.'
        );
        return;
      }

      setButtonLoading(
        button,
        true,
        'Hesap oluşturuluyor...'
      );

      const redirectUrl =
        `${window.location.origin}${window.location.pathname}`;

      const { data, error } =
        await client.auth.signUp({
          email,
          password,
          options: {
            emailRedirectTo: redirectUrl,
            data: {
              full_name: name
            }
          }
        });

      setButtonLoading(button, false);

      if (error) {
        setMessage(
          message,
          error.message ||
          'Creator hesabı oluşturulamadı.'
        );
        return;
      }

      if (data.session) {
        currentUser = data.user;

        showView('loading');

        await loadCreator();
        return;
      }

      showView('confirm-email');
    });
  };


  const numberOrNull = (value) => {
    if (
      value === '' ||
      value === null ||
      value === undefined
    ) {
      return null;
    }

    const parsed = Number(value);

    return Number.isFinite(parsed)
      ? Math.max(0, Math.round(parsed))
      : null;
  };


  const initApplication = () => {
    const form =
      qs('#EchApplicationForm', hub);

    const message =
      qs('#EchApplicationMessage', hub);

    if (!form) return;

    form.addEventListener('submit', async (event) => {
      event.preventDefault();

      if (!currentUser) return;

      const button =
        qs('button[type="submit"]', form);

      setMessage(message, '');

      setButtonLoading(
        button,
        true,
        'Başvuru gönderiliyor...'
      );

      const profile = {
        full_name:
          qs('#ech-app-name', hub).value.trim(),

        phone:
          qs('#ech-app-phone', hub).value.trim()
          || null,

        city:
          qs('#ech-app-city', hub).value.trim(),

        instagram:
          qs('#ech-app-instagram', hub).value.trim(),

        instagram_followers:
          numberOrNull(
            qs(
              '#ech-app-instagram-followers',
              hub
            ).value
          ),

        tiktok:
          qs('#ech-app-tiktok', hub).value.trim()
          || null,

        tiktok_followers:
          numberOrNull(
            qs(
              '#ech-app-tiktok-followers',
              hub
            ).value
          ),

        average_views:
          numberOrNull(
            qs('#ech-app-average-views', hub).value
          ),

        content_category:
          qs('#ech-app-category', hub).value,

        hair_type:
          qs('#ech-app-hair', hub).value
          || null,

        content_frequency:
          qs('#ech-app-frequency', hub).value
          || null,

        portfolio_url:
          qs('#ech-app-portfolio', hub).value.trim()
          || null,

        sample_content_1:
          qs('#ech-app-sample-1', hub).value.trim(),

        sample_content_2:
          qs('#ech-app-sample-2', hub).value.trim()
          || null,

        previous_collaborations:
          qs('#ech-app-collabs', hub).value.trim()
          || null,

        about_creator:
          qs('#ech-app-about', hub).value.trim(),

        updated_at:
          new Date().toISOString()
      };

      const {
        error: updateError
      } = await client
        .from('creator_profiles')
        .update(profile)
        .eq('user_id', currentUser.id);

      if (updateError) {
        console.error(updateError);

        setMessage(
          message,
          'Profil kaydedilemedi. Lütfen tekrar dene.'
        );

        setButtonLoading(button, false);

        return;
      }

      const {
        data,
        error: submitError
      } = await client.rpc(
        'submit_creator_application'
      );

      if (submitError) {
        console.error(submitError);

        setMessage(
          message,
          submitError.message?.includes(
            'incomplete'
          )
            ? 'Başvuru için zorunlu alanları tamamlamalısın.'
            : 'Başvuru gönderilemedi. Lütfen tekrar dene.'
        );

        setButtonLoading(button, false);

        return;
      }

      currentProfile = {
        ...currentProfile,
        ...profile
      };

      if (data) {
        currentAccount = data;
      } else {
        const { data: account } =
          await client
            .from('creator_accounts')
            .select('*')
            .eq('user_id', currentUser.id)
            .single();

        currentAccount = account;
      }

      setButtonLoading(button, false);

      void notifyCreatorApplication();
      populatePending();
      showView('pending');
    });
  };


  const initLogout = () => {
    qsa('[data-ech-logout]', hub)
      .forEach((button) => {

        button.addEventListener(
          'click',
          async () => {

            button.disabled = true;

            await client.auth.signOut();

            currentUser = null;
            currentAccount = null;
            currentProfile = null;
            currentAddress = null;
            currentIsAdmin = false;
            portalLoaded = false;
            portalData = { campaigns: [], applications: [], collaborations: [], submissions: [], notifications: [], deliverables: [] };

            button.disabled = false;

            showView('auth');

            window.history.replaceState(
              null,
              '',
              window.location.pathname
            );
          }
        );

      });
  };


  const initRejectedEdit = () => {
    const button =
      qs('[data-edit-application]', hub);

    if (!button) return;

    button.addEventListener('click', () => {
      populateApplication();
      showView('application');
    });
  };


  const initPortalTabs = () => {
    const buttons =
      qsa('[data-hub-tab]', hub);

    const panels =
      qsa('[data-hub-panel]', hub);

    if (!buttons.length) return;

    const setTab = (
      tabName,
      updateHash = true
    ) => {

      const valid =
        panels.some(
          (panel) =>
            panel.dataset.hubPanel === tabName &&
            (tabName !== 'admin' || currentIsAdmin)
        );

      if (!valid) {
        tabName = 'dashboard';
      }

      buttons.forEach((button) => {
        button.classList.toggle(
          'is-active',
          button.dataset.hubTab === tabName
        );
      });

      panels.forEach((panel) => {
        const active =
          panel.dataset.hubPanel === tabName;

        panel.hidden = !active;

        panel.classList.toggle(
          'is-active',
          active
        );
      });

      if (updateHash) {
        history.replaceState(
          null,
          '',
          `${window.location.pathname}#${tabName}`
        );
      }
    };


    buttons.forEach((button) => {
      if (button.dataset.tabReady) return;

      button.dataset.tabReady = 'true';

      button.addEventListener('click', () => {
        setTab(button.dataset.hubTab);
      });
    });


    qsa('[data-open-tab]', hub)
      .forEach((button) => {

        if (button.dataset.tabReady) return;

        button.dataset.tabReady = 'true';

        button.addEventListener('click', () => {
          setTab(button.dataset.openTab);
        });

      });


    const hash =
      window.location.hash.replace('#', '');

    setTab(hash || (currentIsAdmin ? 'admin' : 'dashboard'), false);
  };



  const escapeHtml = (value) => String(value ?? '')
    .replaceAll('&', '&amp;')
    .replaceAll('<', '&lt;')
    .replaceAll('>', '&gt;')
    .replaceAll('"', '&quot;')
    .replaceAll("'", '&#039;');


  const safeUrl = (value) => {
    const url = String(value || '').trim();
    return /^https?:\/\//i.test(url) ? escapeHtml(url) : '';
  };


  const formatDate = (value, withTime = false) => {
    if (!value) return '—';
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return '—';
    return new Intl.DateTimeFormat('tr-TR', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      ...(withTime ? { hour: '2-digit', minute: '2-digit' } : {})
    }).format(date);
  };


  const STATUS_NAMES = {
    draft: 'Taslak', pending: 'İnceleniyor', approved: 'Onaylandı', rejected: 'Güncelleme Gerekli', suspended: 'Askıda',
    open: 'Yayında', paused: 'Duraklatıldı', closed: 'Kapandı', completed: 'Tamamlandı',
    applied: 'Başvuruldu', accepted: 'Kabul Edildi', waitlist: 'Bekleme Listesi', withdrawn: 'Geri Çekildi',
    pr_preparing: 'PR Hazırlanıyor', shipped: 'Kargoda', delivered: 'Teslim Edildi', content_due: 'İçerik Bekleniyor',
    submitted: 'Teslim Edildi', under_review: 'İnceleniyor', revision_requested: 'Revizyon İstendi', cancelled: 'İptal',
    creator_rejected: 'Güncelleme Gerekli'
  };


  const statusName = (value) => STATUS_NAMES[value] || String(value || '—');


  const emptyState = (title, text = '') => `
    <div class="ech-empty-state">
      <div>◇</div><h2>${escapeHtml(title)}</h2>${text ? `<p>${escapeHtml(text)}</p>` : ''}
    </div>`;


  const showPortalMessage = (message, type = 'success') => {
    const element = qs('#EchPortalMessage', hub);
    if (!element) return;
    element.textContent = message;
    element.hidden = false;
    element.className = `ech-toast is-${type}`;
    window.setTimeout(() => { element.hidden = true; }, 4200);
  };


  const setModal = (modal, open) => {
    if (!modal) return;
    modal.hidden = !open;
    document.documentElement.classList.toggle('ech-modal-open', open);
  };


  const populateAddress = () => {
    const address = currentAddress || {};
    const values = {
      'ech-address-name': address.recipient_name || currentProfile?.full_name || '',
      'ech-address-phone': address.phone || currentProfile?.phone || '',
      'ech-address-line1': address.address_line_1 || '',
      'ech-address-line2': address.address_line_2 || '',
      'ech-address-district': address.district || '',
      'ech-address-city': address.city || currentProfile?.city || '',
      'ech-address-province': address.province || currentProfile?.city || '',
      'ech-address-postal': address.postal_code || '',
      'ech-address-country': address.country || 'Türkiye',
      'ech-address-note': address.delivery_note || ''
    };

    Object.entries(values).forEach(([id, value]) => {
      const input = qs(`#${id}`, hub);
      if (input) input.value = value;
    });

    const form = qs('#EchAddressForm', hub);
    const gate = qs('#EchAddressGate', hub);
    const approved = currentAccount?.status === 'approved';

    if (form) {
      qsa('input, textarea, button[type="submit"]', form).forEach((field) => {
        field.disabled = !approved;
      });
    }

    if (gate) {
      gate.textContent = approved
        ? (currentAddress ? 'Adresin kayıtlı. PR gönderiminden önce güncelliğini kontrol edebilirsin.' : 'PR gönderimleri için teslimat adresini şimdi tamamla.')
        : 'Adres alanı, creator başvurusu onaylandıktan sonra açılır.';
    }

    const status = qs('#EchAddressStatus', hub);
    const statusText = qs('#EchAddressStatusText', hub);
    if (status) status.textContent = currentAddress ? 'Adres tamamlandı' : (approved ? 'Adres bekleniyor' : 'Onay sonrası açılır');
    if (statusText) statusText.textContent = currentAddress
      ? `${address.district || ''}${address.district && address.city ? ', ' : ''}${address.city || ''} teslimat profili kayıtlı.`
      : (approved ? 'PR gönderimleri için adresini profil bölümünden tamamla.' : 'Başvurun onaylandığında güvenli adres formu açılır.');
  };


  const loadCreatorPortal = async () => {
    if (!currentUser || (!currentIsAdmin && currentAccount?.status !== 'approved')) return;

    const [campaignsResult, applicationsResult, collaborationsResult, submissionsResult, notificationsResult, deliverablesResult, addressResult] = await Promise.all([
      client.from('creator_campaigns').select('*').order('created_at', { ascending: false }),
      client.from('campaign_applications').select('*').eq('creator_user_id', currentUser.id).order('created_at', { ascending: false }),
      client.from('creator_collaborations').select('*').eq('creator_user_id', currentUser.id).order('created_at', { ascending: false }),
      client.from('content_submissions').select('*').eq('creator_user_id', currentUser.id).order('created_at', { ascending: false }),
      client.from('creator_notifications').select('*').eq('creator_user_id', currentUser.id).order('created_at', { ascending: false }),
      client.from('campaign_deliverables').select('*').order('sort_order', { ascending: true }),
      client.from('creator_addresses').select('*').eq('user_id', currentUser.id).maybeSingle()
    ]);

    const errors = [campaignsResult, applicationsResult, collaborationsResult, submissionsResult, notificationsResult, deliverablesResult]
      .map((result) => result.error).filter(Boolean);
    if (errors.length) console.error('Creator portal data error:', errors);

    portalData = {
      campaigns: campaignsResult.data || [],
      applications: applicationsResult.data || [],
      collaborations: collaborationsResult.data || [],
      submissions: submissionsResult.data || [],
      notifications: notificationsResult.data || [],
      deliverables: deliverablesResult.data || []
    };
    if (!addressResult.error) currentAddress = addressResult.data || null;

    renderCreatorPortal();
    if (currentIsAdmin) await loadAdminPortal();
    portalLoaded = true;
  };


  const renderCreatorPortal = () => {
    renderCampaignMarket();
    renderMyApplications();
    renderCollaborations();
    renderContentCenter();
    renderNotifications();
    populateAddress();
  };


  const renderCampaignMarket = () => {
    const container = qs('#EchCampaignGrid', hub);
    if (!container) return;
    const campaigns = portalData.campaigns.filter((campaign) => campaign.status === 'open');
    if (!campaigns.length) {
      container.innerHTML = emptyState('Şu anda açık kampanya yok.', 'Yeni Elsa projeleri yayınlandığında burada görünecek.');
      return;
    }

    container.innerHTML = campaigns.map((campaign) => {
      const existing = portalData.applications.find((item) => item.campaign_id === campaign.id && item.status !== 'withdrawn');
      const platforms = (campaign.platforms || []).map((item) => `<span>${escapeHtml(item)}</span>`).join('');
      const image = safeUrl(campaign.hero_image_url);
      return `
        <article class="ech-campaign-card">
          ${image ? `<div class="ech-campaign-image" style="background-image:url('${image}')"></div>` : '<div class="ech-campaign-image ech-campaign-image-fallback"><span>ELSA</span></div>'}
          <div class="ech-campaign-body">
            <div class="ech-campaign-meta"><span>${escapeHtml(String(campaign.campaign_type || 'pr').toUpperCase())}</span><span>${escapeHtml(LEVEL_NAMES[campaign.required_level] || 'New Creator')}</span></div>
            <h2>${escapeHtml(campaign.title)}</h2>
            <p>${escapeHtml(campaign.short_description || 'Detaylar Elsa Creator ekibi tarafından paylaşılacaktır.')}</p>
            ${campaign.product_name ? `<div class="ech-campaign-product"><small>ÜRÜN / SERİ</small><strong>${escapeHtml(campaign.product_name)}</strong></div>` : ''}
            ${platforms ? `<div class="ech-chip-row">${platforms}</div>` : ''}
            <div class="ech-campaign-facts">
              <div><small>SON BAŞVURU</small><strong>${formatDate(campaign.application_deadline)}</strong></div>
              <div><small>KONTENJAN</small><strong>${campaign.slots ? escapeHtml(campaign.slots) : 'Açık'}</strong></div>
            </div>
            ${campaign.reward_description ? `<p class="ech-reward">${escapeHtml(campaign.reward_description)}</p>` : ''}
            <button type="button" class="ech-btn ${existing ? 'ech-btn-light' : 'ech-btn-primary'} ech-btn-full" data-action="open-apply" data-id="${escapeHtml(campaign.id)}" ${existing ? 'disabled' : ''}>${existing ? statusName(existing.status) : 'Kampanyaya Başvur →'}</button>
          </div>
        </article>`;
    }).join('');
  };


  const renderMyApplications = () => {
    const container = qs('#EchMyApplications', hub);
    if (!container) return;
    if (!portalData.applications.length) {
      container.innerHTML = emptyState('Henüz kampanya başvurun yok.', 'Campaign Market içinden sana uygun projeyi seçebilirsin.');
      return;
    }
    container.innerHTML = portalData.applications.map((application) => {
      const campaign = portalData.campaigns.find((item) => item.id === application.campaign_id) || {};
      const canWithdraw = ['applied', 'waitlist'].includes(application.status);
      return `
        <article class="ech-list-card">
          <div><span class="ech-status is-${escapeHtml(application.status)}">${escapeHtml(statusName(application.status))}</span><h2>${escapeHtml(campaign.title || 'Elsa Kampanyası')}</h2><p>${escapeHtml(application.pitch || 'Başvuru notu eklenmedi.')}</p></div>
          <div class="ech-list-card-side"><small>BAŞVURU</small><strong>${formatDate(application.created_at)}</strong>${canWithdraw ? `<button type="button" class="ech-text-button" data-action="withdraw-application" data-id="${escapeHtml(application.id)}">Başvuruyu geri çek</button>` : ''}</div>
        </article>`;
    }).join('');
  };


  const renderCollaborations = () => {
    const container = qs('#EchCollaborations', hub);
    if (!container) return;
    if (!portalData.collaborations.length) {
      container.innerHTML = emptyState('Aktif PR gönderimin yok.', 'Kabul edilen kampanyalar ve kargo bilgileri burada görünür.');
      return;
    }
    container.innerHTML = portalData.collaborations.map((collab) => {
      const campaign = portalData.campaigns.find((item) => item.id === collab.campaign_id) || {};
      const tracking = safeUrl(collab.tracking_url);
      return `
        <article class="ech-list-card ech-pr-card">
          <div><span class="ech-status is-${escapeHtml(collab.status)}">${escapeHtml(statusName(collab.status))}</span><h2>${escapeHtml(campaign.title || 'Elsa İş Birliği')}</h2><p>${escapeHtml(collab.creator_visible_note || 'Süreç güncellemeleri Elsa ekibi tarafından burada paylaşılır.')}</p></div>
          <div class="ech-pr-timeline"><div><small>KARGO</small><strong>${escapeHtml(collab.carrier || 'Hazırlanıyor')}</strong></div><div><small>TAKİP NO</small><strong>${escapeHtml(collab.tracking_number || '—')}</strong></div><div><small>İÇERİK SONU</small><strong>${formatDate(collab.content_due_at)}</strong></div>${tracking ? `<a href="${tracking}" target="_blank" rel="noopener" class="ech-text-button">Kargoyu Takip Et ↗</a>` : ''}</div>
        </article>`;
    }).join('');
  };


  const renderContentCenter = () => {
    const container = qs('#EchContentCenter', hub);
    if (!container) return;
    const eligible = portalData.collaborations.filter((item) => ['delivered', 'content_due', 'submitted', 'under_review', 'revision_requested'].includes(item.status));
    const submissionCards = portalData.submissions.map((submission) => {
      const url = safeUrl(submission.external_url);
      return `<article class="ech-list-card"><div><span class="ech-status is-${escapeHtml(submission.status)}">${escapeHtml(statusName(submission.status))}</span><h2>${escapeHtml(submission.platform || 'İçerik Teslimi')}</h2><p>${escapeHtml(submission.revision_message || submission.note || 'Elsa incelemesi bekleniyor.')}</p></div><div class="ech-list-card-side"><small>TESLİM</small><strong>${formatDate(submission.submitted_at)}</strong>${url ? `<a class="ech-text-button" href="${url}" target="_blank" rel="noopener">İçeriği Aç ↗</a>` : ''}</div></article>`;
    }).join('');
    const submitCards = eligible.map((collab) => {
      const campaign = portalData.campaigns.find((item) => item.id === collab.campaign_id) || {};
      return `<article class="ech-action-card"><div><span>YENİ TESLİM</span><h2>${escapeHtml(campaign.title || 'Elsa İş Birliği')}</h2><p>İçerik linkini ve notlarını Elsa ekibine gönder.</p></div><button type="button" class="ech-btn ech-btn-primary" data-action="open-content" data-id="${escapeHtml(collab.id)}">İçerik Teslim Et →</button></article>`;
    }).join('');
    container.innerHTML = submitCards + submissionCards || emptyState('Henüz içerik teslim etmedin.', 'Teslime hazır iş birliklerin burada açılır.');
  };


  const renderNotifications = () => {
    const container = qs('#EchNotifications', hub);
    if (!container) return;
    const items = portalData.notifications.slice(0, 3);
    container.innerHTML = items.length ? items.map((item) => `<div><strong>${escapeHtml(item.title)}</strong><span>${formatDate(item.created_at)}</span></div>`).join('') : '<p>Yeni bildirimin yok.</p>';
  };


  let adminCalendarDate = new Date();
  let adminCreatorSearch = '';


  const loadAdminPortal = async () => {
    if (!currentIsAdmin) return;
    const results = await Promise.all([
      client.from('creator_accounts').select('*').order('application_submitted_at', { ascending: false, nullsFirst: false }),
      client.from('creator_profiles').select('*'),
      client.from('creator_addresses').select('*'),
      client.from('creator_campaigns').select('*').order('created_at', { ascending: false }),
      client.from('campaign_applications').select('*').order('created_at', { ascending: false }),
      client.from('creator_collaborations').select('*').order('created_at', { ascending: false }),
      client.from('content_submissions').select('*').order('created_at', { ascending: false }),
      client.from('creator_admin_profiles').select('*'),
      client.from('creator_collaboration_finance').select('*'),
      client.from('creator_shipment_items').select('*').order('created_at', { ascending: true }),
      client.from('creator_content_metrics').select('*')
    ]);
    const errors = results.map((result) => result.error).filter(Boolean);
    if (errors.length) {
      console.error('Creator OS data error:', errors);
      showPortalMessage('Yönetim verilerinin bir bölümü yüklenemedi.', 'error');
    }
    const [accounts, profiles, addresses, campaigns, applications, collaborations, submissions, adminProfiles, finances, shipmentItems, contentMetrics] = results;
    adminData = {
      accounts: accounts.data || [], profiles: profiles.data || [], addresses: addresses.data || [],
      campaigns: campaigns.data || [], applications: applications.data || [], collaborations: collaborations.data || [],
      submissions: submissions.data || [], adminProfiles: adminProfiles.data || [], finances: finances.data || [],
      shipmentItems: shipmentItems.data || [], contentMetrics: contentMetrics.data || []
    };
    renderAdminPortal();
  };


  const creatorProfileFor = (userId) => adminData.profiles.find((item) => item.user_id === userId) || {};
  const adminProfileFor = (userId) => adminData.adminProfiles.find((item) => item.creator_user_id === userId) || {};
  const campaignFor = (campaignId) => adminData.campaigns.find((item) => item.id === campaignId) || {};
  const financeFor = (collaborationId) => adminData.finances.find((item) => item.collaboration_id === collaborationId) || {};
  const contentMetricFor = (submissionId) => adminData.contentMetrics.find((item) => item.submission_id === submissionId) || {};
  const numberValue = (value) => Math.max(0, Number(value || 0) || 0);
  const money = (value, currency = 'TRY') => new Intl.NumberFormat('tr-TR', { style: 'currency', currency, maximumFractionDigits: 2 }).format(numberValue(value));
  const dateInputValue = (value) => value ? new Date(value).toISOString().slice(0, 16) : '';
  const selectOptions = (values, selected) => values.map(([value, label]) => `<option value="${value}" ${selected === value ? 'selected' : ''}>${label}</option>`).join('');
  const creatorScore = (adminProfile) => {
    const values = [adminProfile.quality_score, adminProfile.engagement_score, adminProfile.brand_fit_score].filter((value) => value !== null && value !== undefined);
    return values.length ? Math.round(values.reduce((sum, value) => sum + Number(value), 0) / values.length) : 0;
  };


  const setAdminView = (viewName) => {
    const availableViews = qsa('[data-admin-panel]', hub).map((panel) => panel.dataset.adminPanel);
    const targetView = availableViews.includes(viewName) ? viewName : 'overview';
    qsa('[data-admin-view]', hub).forEach((button) => {
      const active = button.dataset.adminView === targetView;
      button.classList.toggle('is-active', active);
      button.setAttribute('aria-selected', active ? 'true' : 'false');
      button.setAttribute('tabindex', active ? '0' : '-1');
    });
    qsa('[data-admin-panel]', hub).forEach((panel) => {
      const active = panel.dataset.adminPanel === targetView;
      panel.hidden = !active;
      panel.classList.toggle('is-active', active);
    });
    const adminPanel = qs('[data-hub-panel="admin"]', hub);
    if (adminPanel) adminPanel.dataset.activeAdminView = targetView;
  };


  const initAdminWorkspace = () => {
    qsa('[data-admin-view]', hub).forEach((button) => {
      if (button.dataset.adminWorkspaceReady === 'true') return;
      button.dataset.adminWorkspaceReady = 'true';
      button.addEventListener('click', (event) => {
        event.preventDefault();
        event.stopPropagation();
        setAdminView(button.dataset.adminView || 'overview');
      });
    });
    const activeButton = qs('[data-admin-view].is-active', hub);
    setAdminView(activeButton?.dataset.adminView || 'overview');
  };


  const renderAdminPortal = () => {
    initAdminWorkspace();
    const accounts = adminData.accounts || [];
    const campaigns = adminData.campaigns || [];
    const collaborations = adminData.collaborations || [];
    const submissions = adminData.submissions || [];
    const pending = accounts.filter((item) => item.status === 'pending').length;
    const approved = accounts.filter((item) => item.status === 'approved').length;
    const published = submissions.filter((item) => item.status === 'approved' || item.published_at).length;
    const delivered = collaborations.filter((item) => ['delivered','content_due','submitted','under_review','revision_requested','approved','completed'].includes(item.status)).length;
    const shareRate = delivered ? Math.min(100, Math.round((published / delivered) * 100)) : 0;
    const reach = adminData.contentMetrics.reduce((sum, item) => sum + numberValue(item.views), 0) || submissions.reduce((sum, item) => sum + numberValue(item.reported_views), 0);
    const values = {
      EchAdminPendingCount: pending,
      EchAdminPendingBadge: pending,
      EchAdminApprovedCount: approved,
      EchAdminOpenCampaignCount: campaigns.filter((item) => item.status === 'open').length,
      EchAdminActiveCollabCount: collaborations.length,
      EchAdminTotalShipments: collaborations.length,
      EchAdminPublishedCount: published,
      EchAdminContentDueCount: collaborations.filter((item) => ['content_due','submitted','under_review','revision_requested'].includes(item.status)).length,
      EchAdminShareRate: `%${shareRate}`,
      EchAdminPreparingCount: collaborations.filter((item) => ['accepted','pr_preparing'].includes(item.status)).length,
      EchAdminReachCount: formatViews(reach)
    };
    Object.entries(values).forEach(([id, value]) => { const element = qs(`#${id}`, hub); if (element) element.textContent = value; });
    const today = qs('#EchAdminToday', hub);
    if (today) today.textContent = new Intl.DateTimeFormat('tr-TR', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' }).format(new Date());
    const greeting = qs('#EchAdminGreeting', hub);
    if (greeting) greeting.textContent = `${approved} onaylı creator ile operasyon hazır`;
    const donut = qs('#EchAdminShareDonut', hub);
    if (donut) { donut.style.setProperty('--value', shareRate); const strong = qs('strong', donut); if (strong) strong.textContent = `%${shareRate}`; }
    renderAdminOverview();
    renderAdminCreatorDirectory();
    renderAdminCreators();
    renderAdminShipments();
    renderAdminCollaborations();
    renderAdminCalendar();
    renderAdminContentAnalytics();
    renderAdminSubmissions();
    renderAdminRoi();
    renderAdminCampaigns();
    renderAdminApplications();
  };


  const renderAdminOverview = () => {
    const trend = qs('#EchAdminTrendChart', hub);
    if (trend) {
      const days = Array.from({ length: 14 }, (_, index) => { const date = new Date(); date.setHours(0,0,0,0); date.setDate(date.getDate() - (13 - index)); return date; });
      const points = days.map((date) => {
        const next = new Date(date); next.setDate(next.getDate() + 1);
        const shipments = adminData.collaborations.filter((item) => { const value = new Date(item.shipped_at || item.created_at); return value >= date && value < next; }).length;
        const shares = adminData.submissions.filter((item) => { const value = new Date(item.published_at || item.submitted_at || item.created_at); return value >= date && value < next && (item.status === 'approved' || item.published_at); }).length;
        return { date, shipments, shares };
      });
      const maximum = Math.max(1, ...points.flatMap((item) => [item.shipments, item.shares]));
      trend.innerHTML = points.map((item) => `<div class="ecos-trend-day"><div class="ecos-trend-bars"><i class="ship" style="height:${Math.max(4, item.shipments / maximum * 100)}%"></i><i class="share" style="height:${Math.max(4, item.shares / maximum * 100)}%"></i></div><span>${item.date.getDate()}.${item.date.getMonth()+1}</span></div>`).join('');
    }
    const pipeline = qs('#EchAdminPipeline', hub);
    if (pipeline) {
      const groups = [
        ['Hazırlanıyor', ['accepted','pr_preparing'], 'is-gold'],
        ['Kargoda', ['shipped'], 'is-blue'],
        ['Teslim edildi', ['delivered'], 'is-mint'],
        ['İçerik bekliyor', ['content_due','submitted','under_review','revision_requested'], 'is-rose'],
        ['Tamamlandı', ['approved','completed'], 'is-ink']
      ];
      const total = Math.max(1, adminData.collaborations.length);
      pipeline.innerHTML = groups.map(([label, statuses, color]) => { const count = adminData.collaborations.filter((item) => statuses.includes(item.status)).length; return `<div><span><i class="${color}"></i>${label}<b>${count}</b></span><em><i style="width:${count / total * 100}%"></i></em></div>`; }).join('');
    }
    const cities = qs('#EchAdminCityChart', hub);
    if (cities) {
      const counts = adminData.profiles.reduce((acc, profile) => { const city = profile.city || 'Belirtilmedi'; acc[city] = (acc[city] || 0) + 1; return acc; }, {});
      const entries = Object.entries(counts).sort((a,b) => b[1]-a[1]).slice(0, 6);
      const maximum = Math.max(1, ...entries.map(([,count]) => count));
      cities.innerHTML = entries.length ? entries.map(([city,count]) => `<div><span>${escapeHtml(city)}<b>${count}</b></span><em><i style="width:${count / maximum * 100}%"></i></em></div>`).join('') : emptyState('Henüz şehir verisi yok.', 'Creator profilleri tamamlandıkça dağılım burada oluşur.');
    }
  };


  const renderAdminCreatorDirectory = () => {
    const container = qs('#EchAdminCreatorDirectory', hub);
    if (!container) return;
    const term = adminCreatorSearch.trim().toLocaleLowerCase('tr-TR');
    const accounts = adminData.accounts.filter((account) => {
      const profile = creatorProfileFor(account.user_id);
      return !term || [profile.full_name, profile.city, profile.instagram, profile.tiktok, account.email].some((value) => String(value || '').toLocaleLowerCase('tr-TR').includes(term));
    });
    const summary = qs('#EchAdminCreatorSummary', hub);
    if (summary) {
      const followers = adminData.profiles.reduce((sum, item) => sum + numberValue(item.instagram_followers) + numberValue(item.tiktok_followers), 0);
      const missingSocial = adminData.profiles.filter((item) => !item.instagram && !item.tiktok).length;
      summary.innerHTML = `<div><span>Toplam kişi</span><strong>${adminData.accounts.length}</strong></div><div><span>Toplam takipçi</span><strong>${formatViews(followers)}</strong></div><div><span>Onaylı</span><strong>${adminData.accounts.filter((item) => item.status === 'approved').length}</strong></div><div><span>Sosyal hesap eksik</span><strong>${missingSocial}</strong></div>`;
    }
    if (!accounts.length) { container.innerHTML = emptyState('Aramayla eşleşen creator yok.', 'Farklı bir isim, şehir veya kullanıcı adı deneyin.'); return; }
    const collabTypes = [['unset','Belirsiz'],['barter','Barter'],['paid','Ücretli'],['affiliate','Affiliate'],['gift','Hediye'],['hybrid','Hibrit']];
    const relations = [['new','Yeni'],['positive','Olumlu'],['neutral','Nötr'],['watch','Takipte'],['blocked','Engelli']];
    container.innerHTML = `<div class="ecos-table ecos-creator-table"><div class="ecos-tr ecos-th"><span>Creator</span><span>Sosyal & erişim</span><span>İş birliği</span><span>Puanlar</span><span>İç not</span><span>İşlem</span></div>${accounts.map((account) => {
      const profile = creatorProfileFor(account.user_id);
      const adminProfile = adminProfileFor(account.user_id);
      const initials = String(profile.full_name || 'EC').split(/\s+/).slice(0,2).map((part) => part[0]).join('').toUpperCase();
      const followers = numberValue(profile.instagram_followers) + numberValue(profile.tiktok_followers);
      const phoneDigits = String(profile.phone || '').replace(/\D/g, '');
      return `<div class="ecos-tr" data-admin-profile-row data-user-id="${escapeHtml(account.user_id)}"><div class="ecos-person"><i>${escapeHtml(initials)}</i><span><strong>${escapeHtml(profile.full_name || 'İsimsiz Creator')}</strong><small>${escapeHtml(profile.city || 'Şehir yok')} · ${escapeHtml(formatCreatorNumber(account.creator_number))}</small><em class="ech-status is-${escapeHtml(account.status)}">${escapeHtml(statusName(account.status))}</em></span></div><div><strong>${formatViews(followers)}</strong><small>@${escapeHtml(profile.instagram || profile.tiktok || 'hesap-yok')}</small></div><div><select data-profile-collaboration>${selectOptions(collabTypes, adminProfile.collaboration_type || 'unset')}</select><select data-profile-relation>${selectOptions(relations, adminProfile.relationship_status || 'new')}</select></div><div class="ecos-score-fields"><label>Kalite<input data-profile-quality type="number" min="0" max="100" value="${adminProfile.quality_score ?? ''}"></label><label>Etkileşim<input data-profile-engagement type="number" min="0" max="100" value="${adminProfile.engagement_score ?? ''}"></label><label>Uyum<input data-profile-fit type="number" min="0" max="100" value="${adminProfile.brand_fit_score ?? ''}"></label><b>${creatorScore(adminProfile)}</b></div><div><textarea data-profile-note placeholder="Yalnızca yönetici notu">${escapeHtml(adminProfile.internal_note || '')}</textarea></div><div class="ecos-row-actions">${phoneDigits ? `<a href="https://wa.me/${phoneDigits}" target="_blank" rel="noopener" aria-label="WhatsApp">WA</a>` : ''}<button type="button" data-action="save-admin-profile">Kaydet</button></div></div>`;
    }).join('')}</div>`;
  };


  const renderAdminCreators = () => {
    const container = qs('#EchAdminCreators', hub);
    if (!container) return;
    const pending = adminData.accounts.filter((account) => account.status === 'pending');
    const summary = qs('#EchAdminApprovalSummary', hub);
    if (summary) {
      const totalFollowers = pending.reduce((sum, account) => { const profile = creatorProfileFor(account.user_id); return sum + numberValue(profile.instagram_followers) + numberValue(profile.tiktok_followers); }, 0);
      summary.innerHTML = `<div><span>Bekleyen</span><strong>${pending.length}</strong></div><div><span>Barter talebi</span><strong>${pending.filter((item) => adminProfileFor(item.user_id).collaboration_type === 'barter').length}</strong></div><div><span>Ücretli talep</span><strong>${pending.filter((item) => ['paid','hybrid'].includes(adminProfileFor(item.user_id).collaboration_type)).length}</strong></div><div><span>Toplam takipçi</span><strong>${formatViews(totalFollowers)}</strong></div>`;
    }
    if (!pending.length) { container.innerHTML = emptyState('İnceleme bekleyen başvuru yok.', 'Yeni başvurular geldiğinde e-posta bildirimiyle birlikte burada görünür.'); return; }
    container.innerHTML = pending.map((account) => {
      const profile = creatorProfileFor(account.user_id);
      const sample = safeUrl(profile.sample_content_1);
      const portfolio = safeUrl(profile.portfolio_url);
      const totalFollowers = numberValue(profile.instagram_followers) + numberValue(profile.tiktok_followers);
      return `<article class="ech-admin-creator ecos-approval-card" data-creator-card data-user-id="${escapeHtml(account.user_id)}"><div class="ech-admin-creator-head"><div><span>${escapeHtml(formatCreatorNumber(account.creator_number))}</span><h2>${escapeHtml(profile.full_name || 'İsimsiz Creator')}</h2><p>${escapeHtml(account.email || '')} · ${escapeHtml(profile.phone || 'Telefon yok')}</p></div><span class="ech-status is-pending">İnceleniyor</span></div><div class="ech-admin-profile-facts"><div><small>TOPLAM TAKİPÇİ</small><strong>${formatViews(totalFollowers)}</strong><span>${escapeHtml(profile.instagram || profile.tiktok || 'Sosyal hesap yok')}</span></div><div><small>ORT. İZLENME</small><strong>${formatViews(profile.average_views)}</strong><span>${escapeHtml(profile.content_category || 'Kategori yok')}</span></div><div><small>ŞEHİR / SAÇ</small><strong>${escapeHtml(profile.city || '—')}</strong><span>${escapeHtml(profile.hair_type || 'Saç tipi yok')}</span></div></div><p class="ech-admin-about">${escapeHtml(profile.about_creator || 'Tanıtım metni eklenmedi.')}</p><div class="ech-link-row">${sample ? `<a href="${sample}" target="_blank" rel="noopener">Örnek içerik ↗</a>` : ''}${portfolio ? `<a href="${portfolio}" target="_blank" rel="noopener">Media kit ↗</a>` : ''}</div><div class="ech-admin-review-controls"><div class="ech-field"><label>Creator seviyesi</label><select data-review-level><option value="new_creator">New Creator</option><option value="elsa_creator">Elsa Creator</option><option value="elsa_pro">Elsa Pro</option><option value="elsa_ambassador">Elsa Ambassador</option></select></div><div class="ech-field"><label>Değerlendirme notu</label><input data-review-note placeholder="Creator'a görünecek açıklama"></div><div class="ech-review-buttons"><button type="button" class="ech-btn ech-btn-primary" data-action="review-creator" data-status="approved">✓ Onayla</button><button type="button" class="ech-btn ech-btn-danger" data-action="review-creator" data-status="rejected">× Güncelleme iste</button></div></div></article>`;
    }).join('');
  };


  const renderAdminShipments = () => {
    const summary = qs('#EchAdminShipmentSummary', hub);
    const board = qs('#EchAdminShipmentBoard', hub);
    if (!board) return;
    const missingAddress = adminData.collaborations.filter((collab) => !adminData.addresses.some((address) => address.user_id === collab.creator_user_id)).length;
    const scanned = adminData.shipmentItems.reduce((sum, item) => sum + numberValue(item.scanned_quantity), 0);
    const quantity = adminData.shipmentItems.reduce((sum, item) => sum + numberValue(item.quantity), 0);
    if (summary) summary.innerHTML = `<div><span>Toplam gönderim</span><strong>${adminData.collaborations.length}</strong></div><div><span>Adres eksik</span><strong>${missingAddress}</strong></div><div><span>Barkod kontrolü</span><strong>${scanned}/${quantity}</strong></div><div><span>Kargoda</span><strong>${adminData.collaborations.filter((item) => item.status === 'shipped').length}</strong></div>`;
    const columns = [
      ['Hazırlanıyor',['accepted','pr_preparing']],['Gönderildi',['shipped']],['Teslim edildi',['delivered']],
      ['İçerik bekliyor',['content_due','submitted','under_review','revision_requested']],['Paylaşıldı',['approved','completed']]
    ];
    board.innerHTML = columns.map(([title, statuses]) => {
      const rows = adminData.collaborations.filter((item) => statuses.includes(item.status));
      return `<section class="ecos-kanban-column"><header><span>${title}</span><b>${rows.length}</b></header><div>${rows.length ? rows.map((collab) => { const profile = creatorProfileFor(collab.creator_user_id); const campaign = campaignFor(collab.campaign_id); const address = adminData.addresses.find((item) => item.user_id === collab.creator_user_id); return `<article><strong>${escapeHtml(profile.full_name || 'Creator')}</strong><span>${escapeHtml(campaign.product_name || campaign.title || 'Elsa PR')}</span><small>${address ? escapeHtml(`${address.district}, ${address.city}`) : '⚠ Adres bekleniyor'}</small></article>`; }).join('') : '<p>Bu aşamada kayıt yok.</p>'}</div></section>`;
    }).join('');
  };


  const renderAdminCollaborations = () => {
    const container = qs('#EchAdminCollaborations', hub);
    if (!container) return;
    if (!adminData.collaborations.length) { container.innerHTML = emptyState('Aktif iş birliği yok.', 'Kabul edilen kampanya başvuruları otomatik olarak buraya düşer.'); return; }
    const statuses = ['accepted','pr_preparing','shipped','delivered','content_due','submitted','under_review','revision_requested','approved','completed','cancelled'];
    container.innerHTML = adminData.collaborations.map((collab) => {
      const profile = creatorProfileFor(collab.creator_user_id);
      const campaign = campaignFor(collab.campaign_id);
      const address = adminData.addresses.find((item) => item.user_id === collab.creator_user_id);
      const items = adminData.shipmentItems.filter((item) => item.collaboration_id === collab.id);
      return `<article class="ech-admin-collab ecos-shipment-detail" data-collab-row data-id="${escapeHtml(collab.id)}"><div class="ech-admin-row"><div><span class="ech-status is-${escapeHtml(collab.status)}">${escapeHtml(statusName(collab.status))}</span><h3>${escapeHtml(profile.full_name || 'Creator')} · ${escapeHtml(campaign.title || 'Elsa İş Birliği')}</h3><p>${address ? escapeHtml(`${address.recipient_name} · ${address.address_line_1}, ${address.district}/${address.city} · ${address.phone}`) : '⚠ Creator adresini henüz tamamlamadı.'}</p></div></div><div class="ecos-package-items">${items.length ? items.map((item) => `<div data-shipment-item data-id="${escapeHtml(item.id)}"><span><strong>${escapeHtml(item.product_name)}</strong><small>${escapeHtml(item.barcode || 'Barkod yok')} · ${money(item.unit_cost)}</small></span><b>${item.scanned_quantity}/${item.quantity}</b><button type="button" data-action="scan-shipment-item" ${item.scanned_quantity >= item.quantity ? 'disabled' : ''}>${item.scanned_quantity >= item.quantity ? '✓ Tamam' : 'Barkod +1'}</button></div>`).join('') : '<p>Pakete henüz ürün eklenmedi.</p>'}</div><div class="ecos-item-form"><input data-item-name placeholder="Ürün adı"><input data-item-barcode placeholder="Barkod"><input data-item-quantity type="number" min="1" value="1" aria-label="Adet"><input data-item-cost type="number" min="0" step="0.01" placeholder="Birim maliyet"><button type="button" data-action="add-shipment-item">Ürün ekle</button></div><div class="ech-logistics-grid"><input data-carrier value="${escapeHtml(collab.carrier || '')}" placeholder="Kargo firması"><input data-tracking value="${escapeHtml(collab.tracking_number || '')}" placeholder="Takip numarası"><input data-tracking-url value="${escapeHtml(collab.tracking_url || '')}" placeholder="Takip linki"><select data-collab-status>${statuses.map((status) => `<option value="${status}" ${collab.status === status ? 'selected' : ''}>${statusName(status)}</option>`).join('')}</select><button type="button" class="ech-btn ech-btn-primary ech-btn-compact" data-action="save-collaboration">Süreci güncelle</button></div></article>`;
    }).join('');
  };


  const renderAdminCalendar = () => {
    const container = qs('#EchAdminCalendar', hub);
    if (!container) return;
    const year = adminCalendarDate.getFullYear();
    const month = adminCalendarDate.getMonth();
    const first = new Date(year, month, 1);
    const last = new Date(year, month + 1, 0);
    const leading = (first.getDay() + 6) % 7;
    const title = qs('#EchAdminCalendarTitle', hub);
    if (title) title.textContent = new Intl.DateTimeFormat('tr-TR', { month: 'long', year: 'numeric' }).format(first);
    const events = [];
    adminData.submissions.forEach((submission) => {
      const profile = creatorProfileFor(submission.creator_user_id);
      const date = submission.published_at || submission.planned_publish_at || submission.submitted_at;
      if (date) events.push({ date: new Date(date), label: `${profile.full_name || 'Creator'} · ${submission.platform || 'İçerik'}`, type: submission.published_at ? 'published' : 'planned' });
    });
    adminData.collaborations.forEach((collab) => { if (collab.content_due_at) { const profile = creatorProfileFor(collab.creator_user_id); events.push({ date: new Date(collab.content_due_at), label: `${profile.full_name || 'Creator'} · teslim`, type: 'due' }); } });
    const monthEvents = events.filter((item) => item.date.getFullYear() === year && item.date.getMonth() === month);
    const summary = qs('#EchAdminCalendarSummary', hub);
    if (summary) summary.innerHTML = `<div><span>Bu ay toplam</span><strong>${monthEvents.length}</strong></div><div><span>Yayınlanan</span><strong>${monthEvents.filter((item) => item.type === 'published').length}</strong></div><div><span>Planlanan</span><strong>${monthEvents.filter((item) => item.type === 'planned').length}</strong></div><div><span>Teslim tarihi</span><strong>${monthEvents.filter((item) => item.type === 'due').length}</strong></div>`;
    const cells = [];
    for (let index = 0; index < leading; index += 1) cells.push('<div class="is-muted"></div>');
    for (let day = 1; day <= last.getDate(); day += 1) {
      const dayEvents = monthEvents.filter((item) => item.date.getDate() === day);
      cells.push(`<div><b>${day}</b>${dayEvents.slice(0,3).map((item) => `<span class="is-${item.type}">${escapeHtml(item.label)}</span>`).join('')}${dayEvents.length > 3 ? `<small>+${dayEvents.length - 3} daha</small>` : ''}</div>`);
    }
    container.innerHTML = `<header>${['Pzt','Sal','Çar','Per','Cum','Cmt','Paz'].map((day) => `<span>${day}</span>`).join('')}</header><div class="ecos-calendar-days">${cells.join('')}</div>`;
  };


  const renderAdminContentAnalytics = () => {
    const summary = qs('#EchAdminContentSummary', hub);
    const analytics = qs('#EchAdminContentAnalytics', hub);
    if (!analytics) return;
    const totals = adminData.contentMetrics.reduce((acc, item) => { ['views','likes','comments','shares','saves'].forEach((key) => acc[key] += numberValue(item[key])); return acc; }, { views:0, likes:0, comments:0, shares:0, saves:0 });
    if (!totals.views) totals.views = adminData.submissions.reduce((sum, item) => sum + numberValue(item.reported_views), 0);
    if (summary) summary.innerHTML = `<div><span>Toplam içerik</span><strong>${adminData.submissions.length}</strong></div><div><span>Toplam izlenme</span><strong>${formatViews(totals.views)}</strong></div><div><span>Toplam beğeni</span><strong>${formatViews(totals.likes)}</strong></div><div><span>Toplam etkileşim</span><strong>${formatViews(totals.likes + totals.comments + totals.shares + totals.saves)}</strong></div>`;
    const platformCounts = adminData.submissions.reduce((acc, item) => { const key = item.platform || 'diğer'; acc[key] = (acc[key] || 0) + 1; return acc; }, {});
    const maxPlatform = Math.max(1, ...Object.values(platformCounts));
    const statusCounts = adminData.submissions.reduce((acc, item) => { acc[item.status] = (acc[item.status] || 0) + 1; return acc; }, {});
    analytics.innerHTML = `<section class="ecos-card"><div class="ecos-card-head"><div><span>PLATFORM</span><h2>İçerik dağılımı</h2></div></div><div class="ecos-platform-bars">${Object.entries(platformCounts).length ? Object.entries(platformCounts).map(([name,count]) => `<div><span>${escapeHtml(name)}<b>${count}</b></span><em><i style="width:${count / maxPlatform * 100}%"></i></em></div>`).join('') : '<p>Yayınlanan içerik geldikçe dağılım oluşur.</p>'}</div></section><section class="ecos-card"><div class="ecos-card-head"><div><span>WORKFLOW</span><h2>İnceleme statüleri</h2></div></div><div class="ecos-status-wheel"><div>${Object.entries(statusCounts).map(([status,count]) => `<span><i class="is-${escapeHtml(status)}"></i>${escapeHtml(statusName(status))}<b>${count}</b></span>`).join('') || '<p>Henüz içerik teslimi yok.</p>'}</div></div></section>`;
  };


  const renderAdminSubmissions = () => {
    const container = qs('#EchAdminSubmissions', hub);
    if (!container) return;
    if (!adminData.submissions.length) { container.innerHTML = emptyState('İncelenecek içerik yok.', 'Creator teslimleri burada bağlantıları ve performans alanlarıyla listelenir.'); return; }
    container.innerHTML = adminData.submissions.map((submission) => {
      const profile = creatorProfileFor(submission.creator_user_id);
      const metric = contentMetricFor(submission.id);
      const url = safeUrl(submission.external_url);
      return `<article class="ech-admin-row ecos-content-row" data-submission-row data-id="${escapeHtml(submission.id)}"><div class="ecos-content-info"><span class="ech-status is-${escapeHtml(submission.status)}">${escapeHtml(statusName(submission.status))}</span><h3>${escapeHtml(profile.full_name || 'Creator')} · ${escapeHtml(submission.platform || 'İçerik')}</h3><p>${escapeHtml(submission.caption || submission.note || 'Not eklenmedi.')}</p>${url ? `<a class="ech-text-button" href="${url}" target="_blank" rel="noopener">İçeriği aç ↗</a>` : ''}</div><div class="ecos-metric-editor"><label>Tür<input data-content-type value="${escapeHtml(submission.content_type || '')}" placeholder="Reels / Story"></label><label>Planlanan<input data-planned-at type="datetime-local" value="${dateInputValue(submission.planned_publish_at)}"></label><label>Yayınlanan<input data-published-at type="datetime-local" value="${dateInputValue(submission.published_at)}"></label><label>İzlenme<input data-metric-views type="number" min="0" value="${numberValue(metric.views || submission.reported_views)}"></label><label>Beğeni<input data-metric-likes type="number" min="0" value="${numberValue(metric.likes)}"></label><label>Yorum<input data-metric-comments type="number" min="0" value="${numberValue(metric.comments)}"></label><label>Paylaşım<input data-metric-shares type="number" min="0" value="${numberValue(metric.shares)}"></label><label>Kaydetme<input data-metric-saves type="number" min="0" value="${numberValue(metric.saves)}"></label><button type="button" data-action="save-content-metrics">Metrikleri kaydet</button></div><div class="ech-review-buttons"><button type="button" class="ech-btn ech-btn-primary ech-btn-compact" data-action="review-submission" data-status="approved">Onayla</button><button type="button" class="ech-btn ech-btn-danger ech-btn-compact" data-action="review-submission" data-status="revision_requested">Revizyon</button></div></article>`;
    }).join('');
  };


  const renderAdminRoi = () => {
    const summary = qs('#EchAdminRoiSummary', hub);
    const container = qs('#EchAdminRoi', hub);
    if (!container) return;
    const totalCost = adminData.finances.reduce((sum, item) => sum + numberValue(item.product_cost) + numberValue(item.shipping_cost) + numberValue(item.creator_fee) + numberValue(item.other_cost), 0);
    const revenue = adminData.finances.reduce((sum, item) => sum + numberValue(item.attributed_revenue), 0);
    const views = adminData.contentMetrics.reduce((sum, item) => sum + numberValue(item.views), 0) || adminData.submissions.reduce((sum, item) => sum + numberValue(item.reported_views), 0);
    const engagement = adminData.contentMetrics.reduce((sum, item) => sum + numberValue(item.likes) + numberValue(item.comments) + numberValue(item.shares) + numberValue(item.saves), 0);
    const cpm = views ? totalCost / views * 1000 : 0;
    const cpe = engagement ? totalCost / engagement : 0;
    const roi = totalCost ? (revenue - totalCost) / totalCost * 100 : 0;
    if (summary) summary.innerHTML = `<div><span>Toplam harcama</span><strong>${money(totalCost)}</strong></div><div><span>Toplam izlenme</span><strong>${formatViews(views)}</strong></div><div><span>CPM</span><strong>${money(cpm)}</strong></div><div><span>Etkileşim maliyeti</span><strong>${money(cpe)}</strong></div><div><span>Atfedilen gelir</span><strong>${money(revenue)}</strong></div><div><span>ROI</span><strong>${roi.toFixed(1)}%</strong></div>`;
    if (!adminData.collaborations.length) { container.innerHTML = emptyState('ROI kaydı için iş birliği bekleniyor.', 'İlk gönderim açıldığında maliyet ve gelir alanları burada oluşur.'); return; }
    const rows = adminData.collaborations.map((collab) => {
      const profile = creatorProfileFor(collab.creator_user_id);
      const campaign = campaignFor(collab.campaign_id);
      const finance = financeFor(collab.id);
      const submissions = adminData.submissions.filter((item) => item.collaboration_id === collab.id);
      const collabViews = submissions.reduce((sum, submission) => sum + numberValue(contentMetricFor(submission.id).views || submission.reported_views), 0);
      const cost = numberValue(finance.product_cost) + numberValue(finance.shipping_cost) + numberValue(finance.creator_fee) + numberValue(finance.other_cost);
      const collabRevenue = numberValue(finance.attributed_revenue);
      return `<article data-finance-row data-id="${escapeHtml(collab.id)}"><div class="ecos-roi-person"><strong>${escapeHtml(profile.full_name || 'Creator')}</strong><span>${escapeHtml(campaign.title || 'Elsa İş Birliği')} · ${formatViews(collabViews)} izlenme</span></div><div class="ecos-finance-fields"><label>Ürün<input data-finance-product type="number" min="0" step="0.01" value="${numberValue(finance.product_cost)}"></label><label>Lojistik<input data-finance-shipping type="number" min="0" step="0.01" value="${numberValue(finance.shipping_cost)}"></label><label>Creator<input data-finance-fee type="number" min="0" step="0.01" value="${numberValue(finance.creator_fee)}"></label><label>Diğer<input data-finance-other type="number" min="0" step="0.01" value="${numberValue(finance.other_cost)}"></label><label>Gelir<input data-finance-revenue type="number" min="0" step="0.01" value="${collabRevenue}"></label><button type="button" data-action="save-finance">Kaydet</button></div><div class="ecos-roi-bars"><span>Maliyet <i><b style="width:${Math.min(100, cost / Math.max(cost,collabRevenue,1) * 100)}%"></b></i>${money(cost)}</span><span>Gelir <i><b class="is-revenue" style="width:${Math.min(100, collabRevenue / Math.max(cost,collabRevenue,1) * 100)}%"></b></i>${money(collabRevenue)}</span></div></article>`;
    }).join('');
    container.innerHTML = `<section class="ecos-roi-chart"><div class="ecos-card-head"><div><span>İŞ BİRLİĞİ KARŞILAŞTIRMASI</span><h2>Maliyet & atfedilen gelir</h2></div></div>${rows}</section>`;
  };


  const renderAdminCampaigns = () => {
    const container = qs('#EchAdminCampaigns', hub);
    if (!container) return;
    if (!adminData.campaigns.length) { container.innerHTML = emptyState('Henüz kampanya yok.', 'Stüdyodan ilk taslağı oluşturabilirsiniz.'); return; }
    container.innerHTML = adminData.campaigns.map((campaign) => `<article class="ech-admin-row" data-campaign-row data-id="${escapeHtml(campaign.id)}"><div><span class="ech-status is-${escapeHtml(campaign.status)}">${escapeHtml(statusName(campaign.status))}</span><h3>${escapeHtml(campaign.title)}</h3><p>${escapeHtml(campaign.short_description || '')}</p></div><div class="ech-inline-control"><select data-campaign-status>${['draft','open','paused','closed','completed'].map((status) => `<option value="${status}" ${campaign.status === status ? 'selected' : ''}>${statusName(status)}</option>`).join('')}</select><button type="button" class="ech-btn ech-btn-light ech-btn-compact" data-action="save-campaign-status">Kaydet</button></div></article>`).join('');
  };


  const renderAdminApplications = () => {
    const container = qs('#EchAdminApplications', hub);
    if (!container) return;
    if (!adminData.applications.length) { container.innerHTML = emptyState('Kampanya başvurusu yok.', 'Yayınlanan kampanyalara gelen başvurular burada listelenir.'); return; }
    container.innerHTML = adminData.applications.map((application) => { const profile = creatorProfileFor(application.creator_user_id); const campaign = campaignFor(application.campaign_id); const canReview = ['applied','waitlist'].includes(application.status); return `<article class="ech-admin-row" data-application-row data-id="${escapeHtml(application.id)}"><div><span class="ech-status is-${escapeHtml(application.status)}">${escapeHtml(statusName(application.status))}</span><h3>${escapeHtml(campaign.title || 'Elsa Kampanyası')}</h3><p><strong>${escapeHtml(profile.full_name || 'Creator')}</strong> · ${escapeHtml(application.pitch || 'Fikir notu yok')}</p></div>${canReview ? '<div class="ech-review-buttons"><button type="button" class="ech-btn ech-btn-primary ech-btn-compact" data-action="review-campaign-application" data-status="accepted">Kabul et</button><button type="button" class="ech-btn ech-btn-danger ech-btn-compact" data-action="review-campaign-application" data-status="rejected">Reddet</button></div>' : ''}</article>`; }).join('');
  };


  const slugifyCampaign = (value) => String(value || '')
    .toLocaleLowerCase('tr-TR').replaceAll('ı','i').replaceAll('ğ','g').replaceAll('ü','u').replaceAll('ş','s').replaceAll('ö','o').replaceAll('ç','c')
    .normalize('NFD').replace(/[\u0300-\u036f]/g, '').replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '').slice(0, 60);


  const initPortalActions = () => {
    if (hub.dataset.portalActionsReady) return;
    hub.dataset.portalActionsReady = 'true';

    hub.addEventListener('click', async (event) => {
      const button = event.target.closest('[data-action]');
      if (!button || !hub.contains(button)) return;
      const action = button.dataset.action;

      if (action === 'close-modal') {
        setModal(button.closest('.ech-modal'), false);
        return;
      }

      if (action === 'open-apply') {
        const campaign = portalData.campaigns.find((item) => item.id === button.dataset.id);
        if (!campaign) return;
        qs('#ech-apply-campaign-id', hub).value = campaign.id;
        qs('#EchCampaignModalTitle', hub).textContent = campaign.title;
        qs('#EchCampaignModalDescription', hub).textContent = campaign.short_description || 'Başvuru bilgilerini tamamla.';
        setMessage(qs('#EchCampaignApplyMessage', hub), '');
        setModal(qs('#EchCampaignModal', hub), true);
        return;
      }

      if (action === 'withdraw-application') {
        setButtonLoading(button, true, 'Geri çekiliyor...');
        const { error } = await client.rpc('withdraw_creator_campaign_application', { p_application_id: button.dataset.id });
        if (error) showPortalMessage('Başvuru geri çekilemedi.', 'error');
        else { showPortalMessage('Başvuru geri çekildi.'); await loadCreatorPortal(); }
        setButtonLoading(button, false);
        return;
      }

      if (action === 'open-content') {
        qs('#ech-content-collaboration-id', hub).value = button.dataset.id;
        setMessage(qs('#EchContentSubmitMessage', hub), '');
        setModal(qs('#EchContentModal', hub), true);
        return;
      }

      if (action === 'admin-view') {
        setAdminView(button.dataset.adminView || 'overview');
        return;
      }

      if (action === 'calendar-shift') {
        adminCalendarDate = new Date(adminCalendarDate.getFullYear(), adminCalendarDate.getMonth() + Number(button.dataset.shift || 0), 1);
        renderAdminCalendar();
        return;
      }

      if (action === 'refresh-admin') {
        setButtonLoading(button, true, 'Yükleniyor...');
        await loadAdminPortal();
        setButtonLoading(button, false);
        return;
      }

      if (action === 'save-admin-profile') {
        const row = button.closest('[data-admin-profile-row]');
        if (!row) return;
        const optionalScore = (selector) => {
          const value = qs(selector, row).value;
          return value === '' ? null : Math.min(100, Math.max(0, Number(value)));
        };
        const payload = {
          creator_user_id: row.dataset.userId,
          collaboration_type: qs('[data-profile-collaboration]', row).value,
          relationship_status: qs('[data-profile-relation]', row).value,
          quality_score: optionalScore('[data-profile-quality]'),
          engagement_score: optionalScore('[data-profile-engagement]'),
          brand_fit_score: optionalScore('[data-profile-fit]'),
          internal_note: qs('[data-profile-note]', row).value.trim() || null
        };
        setButtonLoading(button, true, 'Kaydediliyor...');
        const { error } = await client.from('creator_admin_profiles').upsert(payload, { onConflict: 'creator_user_id' });
        if (error) showPortalMessage('Creator yönetim profili kaydedilemedi.', 'error');
        else { showPortalMessage('Creator puanları ve yönetici notu kaydedildi.'); await loadAdminPortal(); }
        setButtonLoading(button, false);
        return;
      }

      if (action === 'add-shipment-item') {
        const row = button.closest('[data-collab-row]');
        if (!row) return;
        const productName = qs('[data-item-name]', row).value.trim();
        if (!productName) { showPortalMessage('Pakete eklemek için ürün adını yazın.', 'error'); return; }
        const payload = {
          collaboration_id: row.dataset.id,
          product_name: productName,
          barcode: qs('[data-item-barcode]', row).value.trim() || null,
          quantity: Math.max(1, Number(qs('[data-item-quantity]', row).value || 1)),
          unit_cost: numberValue(qs('[data-item-cost]', row).value)
        };
        setButtonLoading(button, true, 'Ekleniyor...');
        const { error } = await client.from('creator_shipment_items').insert(payload);
        if (error) showPortalMessage('Paket ürünü eklenemedi.', 'error');
        else { showPortalMessage('Ürün paket listesine eklendi.'); await loadAdminPortal(); }
        setButtonLoading(button, false);
        return;
      }

      if (action === 'scan-shipment-item') {
        const itemRow = button.closest('[data-shipment-item]');
        const item = adminData.shipmentItems.find((record) => record.id === itemRow?.dataset.id);
        if (!item) return;
        const scannedQuantity = Math.min(Number(item.quantity), Number(item.scanned_quantity || 0) + 1);
        setButtonLoading(button, true, 'Okundu');
        const { error } = await client.from('creator_shipment_items').update({ scanned_quantity: scannedQuantity }).eq('id', item.id);
        if (error) showPortalMessage('Barkod kontrolü kaydedilemedi.', 'error');
        else { showPortalMessage(scannedQuantity >= Number(item.quantity) ? 'Ürün barkod kontrolü tamamlandı.' : 'Barkod adedi güncellendi.'); await loadAdminPortal(); }
        setButtonLoading(button, false);
        return;
      }

      if (action === 'save-finance') {
        const row = button.closest('[data-finance-row]');
        if (!row) return;
        const payload = {
          collaboration_id: row.dataset.id,
          product_cost: numberValue(qs('[data-finance-product]', row).value),
          shipping_cost: numberValue(qs('[data-finance-shipping]', row).value),
          creator_fee: numberValue(qs('[data-finance-fee]', row).value),
          other_cost: numberValue(qs('[data-finance-other]', row).value),
          attributed_revenue: numberValue(qs('[data-finance-revenue]', row).value),
          updated_by: currentUser.id
        };
        setButtonLoading(button, true, 'Kaydediliyor...');
        const { error } = await client.from('creator_collaboration_finance').upsert(payload, { onConflict: 'collaboration_id' });
        if (error) showPortalMessage('Maliyet kaydı güncellenemedi.', 'error');
        else { showPortalMessage('ROI maliyet ve gelir kaydı güncellendi.'); await loadAdminPortal(); }
        setButtonLoading(button, false);
        return;
      }

      if (action === 'save-content-metrics') {
        const row = button.closest('[data-submission-row]');
        if (!row) return;
        const plannedValue = qs('[data-planned-at]', row).value;
        const publishedValue = qs('[data-published-at]', row).value;
        const submissionPayload = {
          content_type: qs('[data-content-type]', row).value.trim() || null,
          planned_publish_at: plannedValue ? new Date(plannedValue).toISOString() : null,
          published_at: publishedValue ? new Date(publishedValue).toISOString() : null
        };
        const metricPayload = {
          submission_id: row.dataset.id,
          views: numberValue(qs('[data-metric-views]', row).value),
          likes: numberValue(qs('[data-metric-likes]', row).value),
          comments: numberValue(qs('[data-metric-comments]', row).value),
          shares: numberValue(qs('[data-metric-shares]', row).value),
          saves: numberValue(qs('[data-metric-saves]', row).value),
          source: 'manual',
          synced_at: new Date().toISOString(),
          updated_by: currentUser.id
        };
        setButtonLoading(button, true, 'Kaydediliyor...');
        const [submissionResult, metricResult] = await Promise.all([
          client.from('content_submissions').update(submissionPayload).eq('id', row.dataset.id),
          client.from('creator_content_metrics').upsert(metricPayload, { onConflict: 'submission_id' })
        ]);
        if (submissionResult.error || metricResult.error) showPortalMessage('İçerik metrikleri güncellenemedi.', 'error');
        else { showPortalMessage('İçerik takvimi ve performans metrikleri güncellendi.'); await loadAdminPortal(); }
        setButtonLoading(button, false);
        return;
      }

      if (action === 'review-creator') {
        const card = button.closest('[data-creator-card]');
        const status = button.dataset.status;
        const payload = {
          status,
          level: qs('[data-review-level]', card).value,
          review_note: qs('[data-review-note]', card).value.trim() || null,
          reviewed_at: new Date().toISOString(),
          reviewed_by: currentUser.id,
          approved_at: status === 'approved' ? new Date().toISOString() : null
        };
        setButtonLoading(button, true, status === 'approved' ? 'Onaylanıyor...' : 'Kaydediliyor...');
        const { error } = await client.from('creator_accounts').update(payload).eq('user_id', card.dataset.userId);
        if (error) showPortalMessage('Creator değerlendirmesi kaydedilemedi.', 'error');
        else { showPortalMessage(status === 'approved' ? 'Creator onaylandı; adres alanı açıldı.' : 'Güncelleme talebi creator hesabına iletildi.'); await loadAdminPortal(); }
        setButtonLoading(button, false);
        return;
      }

      if (action === 'save-campaign-status') {
        const row = button.closest('[data-campaign-row]');
        const status = qs('[data-campaign-status]', row).value;
        setButtonLoading(button, true, 'Kaydediliyor...');
        const { error } = await client.from('creator_campaigns').update({ status }).eq('id', row.dataset.id);
        if (error) showPortalMessage('Kampanya durumu güncellenemedi.', 'error');
        else { showPortalMessage('Kampanya durumu güncellendi.'); await loadCreatorPortal(); }
        setButtonLoading(button, false);
        return;
      }

      if (action === 'review-campaign-application') {
        const row = button.closest('[data-application-row]');
        const status = button.dataset.status;
        setButtonLoading(button, true, 'Kaydediliyor...');
        const { error } = await client.from('campaign_applications').update({ status, reviewed_at: new Date().toISOString() }).eq('id', row.dataset.id);
        if (error) showPortalMessage('Kampanya başvurusu güncellenemedi.', 'error');
        else { showPortalMessage(status === 'accepted' ? 'Başvuru kabul edildi ve iş birliği açıldı.' : 'Başvuru reddedildi.'); await loadAdminPortal(); }
        setButtonLoading(button, false);
        return;
      }

      if (action === 'save-collaboration') {
        const row = button.closest('[data-collab-row]');
        const status = qs('[data-collab-status]', row).value;
        const payload = {
          status,
          carrier: qs('[data-carrier]', row).value.trim() || null,
          tracking_number: qs('[data-tracking]', row).value.trim() || null,
          tracking_url: qs('[data-tracking-url]', row).value.trim() || null,
          shipped_at: status === 'shipped' ? new Date().toISOString() : undefined,
          delivered_at: status === 'delivered' ? new Date().toISOString() : undefined,
          completed_at: status === 'completed' ? new Date().toISOString() : undefined
        };
        Object.keys(payload).forEach((key) => payload[key] === undefined && delete payload[key]);
        setButtonLoading(button, true, 'Güncelleniyor...');
        const { error } = await client.from('creator_collaborations').update(payload).eq('id', row.dataset.id);
        if (error) showPortalMessage('Gönderim süreci güncellenemedi.', 'error');
        else { showPortalMessage('İş birliği süreci güncellendi.'); await loadAdminPortal(); }
        setButtonLoading(button, false);
        return;
      }

      if (action === 'review-submission') {
        const row = button.closest('[data-submission-row]');
        const status = button.dataset.status;
        setButtonLoading(button, true, 'Kaydediliyor...');
        const { error } = await client.from('content_submissions').update({ status, reviewed_at: new Date().toISOString() }).eq('id', row.dataset.id);
        if (error) showPortalMessage('İçerik durumu güncellenemedi.', 'error');
        else { showPortalMessage(status === 'approved' ? 'İçerik onaylandı.' : 'İçerik için revizyon istendi.'); await loadAdminPortal(); }
        setButtonLoading(button, false);
      }
    });

    hub.addEventListener('input', (event) => {
      if (!event.target.matches('[data-admin-search]')) return;
      adminCreatorSearch = event.target.value || '';
      renderAdminCreatorDirectory();
    });

    const addressForm = qs('#EchAddressForm', hub);
    addressForm?.addEventListener('submit', async (event) => {
      event.preventDefault();
      if (currentAccount?.status !== 'approved') return;
      const button = qs('button[type="submit"]', addressForm);
      const message = qs('#EchAddressMessage', hub);
      setButtonLoading(button, true, 'Adres kaydediliyor...');
      setMessage(message, '');
      const address = {
        user_id: currentUser.id,
        recipient_name: qs('#ech-address-name', hub).value.trim(),
        phone: qs('#ech-address-phone', hub).value.trim(),
        address_line_1: qs('#ech-address-line1', hub).value.trim(),
        address_line_2: qs('#ech-address-line2', hub).value.trim() || null,
        district: qs('#ech-address-district', hub).value.trim(),
        city: qs('#ech-address-city', hub).value.trim(),
        province: qs('#ech-address-province', hub).value.trim(),
        postal_code: qs('#ech-address-postal', hub).value.trim() || null,
        country: qs('#ech-address-country', hub).value.trim() || 'Türkiye',
        delivery_note: qs('#ech-address-note', hub).value.trim() || null
      };
      const { data, error } = await client.from('creator_addresses').upsert(address, { onConflict: 'user_id' }).select().single();
      if (error) setMessage(message, 'Adres kaydedilemedi. Alanları kontrol edip tekrar dene.');
      else { currentAddress = data; setMessage(message, 'Teslimat adresin güvenle kaydedildi.', 'success'); populateAddress(); }
      setButtonLoading(button, false);
    });

    const applyForm = qs('#EchCampaignApplyForm', hub);
    applyForm?.addEventListener('submit', async (event) => {
      event.preventDefault();
      const button = qs('button[type="submit"]', applyForm);
      const message = qs('#EchCampaignApplyMessage', hub);
      setButtonLoading(button, true, 'Başvuru gönderiliyor...');
      setMessage(message, '');
      const { error } = await client.rpc('apply_to_creator_campaign', {
        p_campaign_id: qs('#ech-apply-campaign-id', hub).value,
        p_pitch: qs('#ech-apply-pitch', hub).value.trim() || null,
        p_product_preference: qs('#ech-apply-preference', hub).value.trim() || null,
        p_proposed_publish_date: qs('#ech-apply-date', hub).value || null
      });
      if (error) setMessage(message, 'Başvuru gönderilemedi. Uygunluk ve tarih bilgilerini kontrol et.');
      else { setModal(qs('#EchCampaignModal', hub), false); applyForm.reset(); showPortalMessage('Kampanya başvurun Elsa ekibine gönderildi.'); await loadCreatorPortal(); }
      setButtonLoading(button, false);
    });

    const contentForm = qs('#EchContentSubmitForm', hub);
    contentForm?.addEventListener('submit', async (event) => {
      event.preventDefault();
      const button = qs('button[type="submit"]', contentForm);
      const message = qs('#EchContentSubmitMessage', hub);
      setButtonLoading(button, true, 'İçerik teslim ediliyor...');
      setMessage(message, '');
      const { error } = await client.rpc('create_creator_content_submission', {
        p_collaboration_id: qs('#ech-content-collaboration-id', hub).value,
        p_deliverable_id: null,
        p_platform: qs('#ech-content-platform', hub).value,
        p_external_url: qs('#ech-content-url', hub).value.trim(),
        p_caption: qs('#ech-content-caption', hub).value.trim() || null,
        p_note: qs('#ech-content-note', hub).value.trim() || null,
        p_reported_views: numberOrNull(qs('#ech-content-views', hub).value)
      });
      if (error) setMessage(message, 'İçerik teslim edilemedi. Linki ve iş birliği durumunu kontrol et.');
      else { setModal(qs('#EchContentModal', hub), false); contentForm.reset(); showPortalMessage('İçeriğin inceleme için teslim edildi.'); await loadCreatorPortal(); }
      setButtonLoading(button, false);
    });

    const campaignForm = qs('#EchAdminCampaignForm', hub);
    campaignForm?.addEventListener('submit', async (event) => {
      event.preventDefault();
      if (!currentIsAdmin) return;
      const button = qs('button[type="submit"]', campaignForm);
      const message = qs('#EchAdminCampaignMessage', hub);
      const title = qs('#ech-admin-title', hub).value.trim();
      const deadline = qs('#ech-admin-deadline', hub).value;
      const slots = numberOrNull(qs('#ech-admin-slots', hub).value);
      const payload = {
        title,
        slug: `${slugifyCampaign(title) || 'elsa-campaign'}-${Date.now().toString(36)}`,
        status: 'draft',
        campaign_type: qs('#ech-admin-type', hub).value,
        short_description: qs('#ech-admin-description', hub).value.trim(),
        product_name: qs('#ech-admin-product', hub).value.trim() || null,
        platforms: qs('#ech-admin-platforms', hub).value.split(',').map((item) => item.trim()).filter(Boolean),
        required_level: qs('#ech-admin-level', hub).value,
        application_deadline: deadline ? new Date(deadline).toISOString() : null,
        slots: slots && slots > 0 ? slots : null,
        reward_description: qs('#ech-admin-reward', hub).value.trim() || null
      };
      setButtonLoading(button, true, 'Taslak oluşturuluyor...');
      setMessage(message, '');
      const { error } = await client.from('creator_campaigns').insert(payload);
      if (error) setMessage(message, 'Kampanya oluşturulamadı. Zorunlu alanları kontrol et.');
      else { campaignForm.reset(); setMessage(message, 'Taslak kampanya oluşturuldu. Yayına almadan önce bilgileri kontrol et.', 'success'); await loadCreatorPortal(); }
      setButtonLoading(button, false);
    });
  };

  const initSession = async () => {
    const {
      data: { session },
      error
    } = await client.auth.getSession();

    if (error) {
      console.error(error);
      showView('auth');
      return;
    }

    if (!session?.user) {
      showView('auth');
      return;
    }

    currentUser = session.user;

    await loadCreator();
  };


  const init = async () => {
    hub =
      document.getElementById(
        'ElsaCreatorHub'
      );

    if (!hub) return;

    if (!window.supabase?.createClient) {
      console.error(
        'Supabase library could not be loaded.'
      );

      showView('auth');
      return;
    }

    const url =
      hub.dataset.supabaseUrl;

    const key =
      hub.dataset.supabaseKey;

    client =
      window.supabase.createClient(
        url,
        key,
        {
          auth: {
            persistSession: true,
            autoRefreshToken: true,
            detectSessionInUrl: true
          }
        }
      );

    initAuthTabs();
    initLogin();
    initRegister();
    initApplication();
    initLogout();
    initRejectedEdit();
    initPortalActions();

    client.auth.onAuthStateChange(
      async (event, session) => {

        if (
          event === 'SIGNED_IN' &&
          session?.user
        ) {
          currentUser = session.user;
        }

        if (event === 'SIGNED_OUT') {
          currentUser = null;
          currentAccount = null;
          currentProfile = null;
          currentAddress = null;
          currentIsAdmin = false;
          portalLoaded = false;
        }
      }
    );

    await initSession();
  };


  if (document.readyState === 'loading') {
    document.addEventListener(
      'DOMContentLoaded',
      init
    );
  } else {
    init();
  }
})();
